import { PublicClientApplication, AccountInfo } from '@azure/msal-browser';
import { loginRequest } from '../authConfig';
import { AF_END_ITERATION, AF_INITIATIV, AF_PLANNING_PRIORITY, AF_PLANNING_STATUS, AF_REL_PARENT, AF_START_ITERATION, AF_TITLE, Ticket } from './Ticket';
import { date_diff_indays } from '../utils/DateUtils';
import * as tConst  from '../Common/Constants'; // Ensure this import matches your project structure
import { TEAMS_LIST, PRIORITY_DRIVERS_ORDER } from '../Common/Constants';
import { TelemetryService } from './TelemetryService';


export const AF_RELATION_PARENT = "System.LinkTypes.Hierarchy-Reverse";
export const AF_RELATION_CHILD = "System.LinkTypes.Hierarchy-Forward";

export interface WorkItemType {
  name: string;
  referenceName: string;
  description: string;
  color: string;
  icon: {
    id: string;
    url: string;
  };
  isDisabled: boolean;
  url: string;
}

export interface WorkItemTypesResponse {
  count: number;
  value: WorkItemType[];
}

export interface WorkItemState {
  name: string;
  color: string;
  category: string;
}

export interface WorkItemStatesResponse {
  count: number;
  value: WorkItemState[];
}

export interface ProjectWorkItemData {
  project: string;
  workItemTypes: WorkItemType[];
  workItemStates: { [typeName: string]: WorkItemState[] };
}

export interface WorklogUser {
  uniqueName: string;
  displayName: string;
  email: string;
  name: string;
  id: string;
}

export interface WorklogItem {
  timestamp: string;
  length: number;
  workItemId: number;
  comment: string;
  user: WorklogUser;
  id: string;
}

export interface WorklogResults {
  data: WorklogItem[];
}

export interface WorkItemWithTimeLog {
  workItem: any;
  timeLogs: WorklogItem[];
  totalDuration: number;
  inheritedFields?: {
    govProject?: { value: string; inherited: boolean };
    capex?: { value: string; inherited: boolean };
    priorityDriver?: { value: string; inherited: boolean };
    product?: { value: string; inherited: boolean };
    investmentClass?: { value: string; inherited: boolean };
  };
}

export interface TeamIterationAttributes {
  startDate: string | null;
  finishDate: string | null;
  timeFrame?: string;
}

export interface TeamSettingsIteration {
  id: string;
  name: string;
  path?: string;
  url: string;
  attributes: TeamIterationAttributes;
  _links?: any;
}

export interface IterationsResponse {
  count: number;
  value: TeamSettingsIteration[];
}

export class AzureDevOpsService {
  private msalInstance: PublicClientApplication;
  private account: AccountInfo | null;
  // In-memory caches to reduce duplicate network calls during a session
  private workItemCache: Map<string, { fetchedAt: number; data: any }>; // keys: `id:<id>` and `url:<url>`
  private areaPathsCache: Map<string, { fetchedAt: number; data: any }>; // key: `<organization>::<project>`
  private iterationsCache: Map<string, { fetchedAt: number; data: any }>; // key: `<organization>::<project>::<team?>`
  private readonly WORKITEM_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes
  private readonly AREA_PATHS_CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes
  private readonly ITERATIONS_CACHE_TTL_MS = 10 * 60 * 1000; // 10 minutes

  constructor(msalInstance: PublicClientApplication, account: AccountInfo | null) {
    this.msalInstance = msalInstance;
    this.account = account;
    this.workItemCache = new Map();
    this.areaPathsCache = new Map();
    this.iterationsCache = new Map();
  }

  /**
   * Clears in-memory caches. Useful for debugging or forcing fresh reads.
   */
  clearCaches() {
    this.workItemCache.clear();
    this.areaPathsCache.clear();
    this.iterationsCache.clear();
  }

  async getAccessToken(): Promise<string | null> {
    if (!this.account) {
      console.warn('No account available. Please log in.');
      return null;
    }
    try {
      const response = await this.msalInstance.acquireTokenSilent({
        ...loginRequest,
        account: this.account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof Error && error.name === "InteractionRequiredAuthError") {
        console.warn("Interaction required. Triggering interactive login.");
        this.msalInstance.acquireTokenRedirect({
          ...loginRequest,
          account: this.account,
        });
        return null;
      } else {
        console.error('Error acquiring token silently:', error);
        return null;
      }
    }
  }

  /**
   * Fetches the list of projects for the given organization from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @returns The list of projects or null if an error occurs.
   */
  async fetchProjects(organization: string): Promise<any> {
    const startTime = Date.now();
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      TelemetryService.trackException(new Error('No access token for fetchProjects'), { organization });
      return null;
    }
    const url = `https://dev.azure.com/${organization}/_apis/projects?api-version=7.0`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      const duration = Date.now() - startTime;
      const success = response.ok;
      
      TelemetryService.trackApiCall('AzureDevOps_FetchProjects', url, duration, success, response.status, {
        organization,
        statusCode: response.status
      });
      
      if (!response.ok) {
        throw new Error(`Error fetching projects from Azure DevOps API: ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      const duration = Date.now() - startTime;
      TelemetryService.trackApiCall('AzureDevOps_FetchProjects', url, duration, false, 0, {
        organization,
        error: (error as Error).message
      });
      TelemetryService.trackException(error as Error, { method: 'fetchProjects', organization });
      console.error('Error fetching projects from Azure DevOps API:', error);
      return null;
    }
  }

  /**
   * Fetches the list of area paths for the given organization and project from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of area paths or null if an error occurs.
   */
  async fetchAreaPaths(organization: string, project: string): Promise<any> {
    const cacheKey = `${organization}::${project}`;
    // In-memory cache
    const memCached = this.areaPathsCache.get(cacheKey);
    if (memCached && (Date.now() - memCached.fetchedAt) < this.AREA_PATHS_CACHE_TTL_MS) {
      return memCached.data;
    }

    // localStorage cache
    try {
      const storageKey = `AzureDevOpsService-areaPaths-${organization}-${project}`;
      const storageTsKey = `${storageKey}-ts`;
      const cachedStr = localStorage.getItem(storageKey);
      const cachedTs = localStorage.getItem(storageTsKey);
      if (cachedStr && cachedTs) {
        const age = Date.now() - parseInt(cachedTs);
        if (age < this.AREA_PATHS_CACHE_TTL_MS) {
          const parsed = JSON.parse(cachedStr);
          this.areaPathsCache.set(cacheKey, { fetchedAt: Date.now(), data: parsed });
          return parsed;
        }
      }
    } catch (err) {
      // ignore localStorage errors
    }

    const startTime = Date.now();
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      TelemetryService.trackException(new Error('No access token for fetchAreaPaths'), { organization, project });
      return null;
    }
    const url = `https://dev.azure.com/${organization}/${project}/_apis/wit/classificationnodes/areas?$depth=2&api-version=7.0`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      const duration = Date.now() - startTime;
      const success = response.ok;
      TelemetryService.trackApiCall('AzureDevOps_FetchAreaPaths', url, duration, success, response.status, {
        organization,
        project,
        statusCode: response.status
      });
      if (!response.ok) {
        throw new Error(`Error fetching area paths from Azure DevOps API: ${response.statusText}`);
      }
      const data = await response.json();
      this.areaPathsCache.set(cacheKey, { fetchedAt: Date.now(), data });
      try {
        const storageKey = `AzureDevOpsService-areaPaths-${organization}-${project}`;
        const storageTsKey = `${storageKey}-ts`;
        localStorage.setItem(storageKey, JSON.stringify(data));
        localStorage.setItem(storageTsKey, Date.now().toString());
      } catch (err) {
        // ignore localStorage errors
      }
      return data;
    } catch (error) {
      const duration = Date.now() - startTime;
      TelemetryService.trackApiCall('AzureDevOps_FetchAreaPaths', url, duration, false, 0, {
        organization,
        project,
        error: (error as Error).message
      });
      TelemetryService.trackException(error as Error, { method: 'fetchAreaPaths', organization, project });
      console.error('Error fetching area paths from Azure DevOps API:', error);
      return null;
    }
  }

  /**
   * Fetches the list of work item types for the given organization and project from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of work item types or null if an error occurs.
   */
  async fetchWorkItemTypes(organization: string, project: string): Promise<WorkItemTypesResponse | null> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }
    const url = `https://dev.azure.com/${organization}/${project}/_apis/wit/workitemtypes?api-version=7.1`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error(`Access denied (${response.status}): User may not have permissions to project ${project}`);
        }
        throw new Error(`Error fetching work item types from Azure DevOps API: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches the list of work item states for a specific work item type.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param workItemType The work item type name.
   * @returns The list of work item states or null if an error occurs.
   */
  async fetchWorkItemStates(organization: string, project: string, workItemType: string): Promise<WorkItemStatesResponse | null> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }
    const url = `https://dev.azure.com/${organization}/${project}/_apis/wit/workitemtypes/${encodeURIComponent(workItemType)}/states?api-version=7.1`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          throw new Error(`Access denied (${response.status}): User may not have permissions to work item type ${workItemType} in project ${project}`);
        }
        throw new Error(`Error fetching work item states from Azure DevOps API: ${response.status} ${response.statusText}`);
      }
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetches work item types and states for all projects in the TEAMS_LIST.
   * @param organization The Azure DevOps organization name.
   * @returns Array of project work item data or empty array if errors occur.
   */
  async fetchAllProjectWorkItemData(organization: string): Promise<ProjectWorkItemData[]> {
    const results: ProjectWorkItemData[] = [];
    const accessibleProjects: string[] = [];
    const inaccessibleProjects: string[] = [];
    
    const uniqueProjects = Array.from(new Set(TEAMS_LIST.map(team => team.project).filter(project => project)));
    uniqueProjects.push(tConst.RND_PLANNING_PROJECT)
    
    for (const project of uniqueProjects) {
      try {
        const typesResponse = await this.fetchWorkItemTypes(organization, project);
        if (!typesResponse || !typesResponse.value) {
          console.warn(`Failed to fetch work item types for project: ${project} - may not have access or project doesn't exist`);
          inaccessibleProjects.push(project);
          continue;
        }
        
        const workItemTypes = typesResponse.value;
        const workItemStates: { [typeName: string]: WorkItemState[] } = {};
        
        for (const workItemType of workItemTypes) {
          try {
            const statesResponse = await this.fetchWorkItemStates(organization, project, workItemType.name);
            if (statesResponse && statesResponse.value) {
              workItemStates[workItemType.name] = statesResponse.value;
            }
          } catch (stateError) {
            console.warn(`Failed to fetch states for work item type ${workItemType.name} in project ${project}:`, stateError);
          }
        }
        
        results.push({
          project,
          workItemTypes,
          workItemStates
        });
        
        accessibleProjects.push(project);
        
      } catch (error: any) {
        const isPermissionError = error?.message?.includes('403') || error?.message?.includes('401') || error?.message?.includes('Unauthorized') || error?.message?.includes('Forbidden');
        
        if (isPermissionError) {
          console.warn(`Access denied to project ${project} - user may not have permissions`);
          inaccessibleProjects.push(project);
        } else {
          console.error(`Error fetching work item data for project ${project}:`, error);
          inaccessibleProjects.push(project);
        }
      }
    }
    
    if (accessibleProjects.length > 0) {
      console.log(`Successfully loaded work item data for ${accessibleProjects.length} projects: ${accessibleProjects.join(', ')}`);
    }
    
    if (inaccessibleProjects.length > 0) {
      console.warn(`Could not access ${inaccessibleProjects.length} projects (permissions or other issues): ${inaccessibleProjects.join(', ')}`);
    }
    
    return results;
  }

  /**
   * Fetches a list of tickets (work items) from a specified project using a WIQL query.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param wiqlQuery The WIQL query string.
   * @returns The list of work items or null if an error occurs.
   */
  async fetchTicketsByQuery(organization: string, project: string, wiqlQuery: string): Promise<any> {
    const startTime = Date.now();
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      console.error('Access token is missing or invalid.');
      TelemetryService.trackException(new Error('No access token for fetchTicketsByQuery'), { organization, project });
      return null;
    }

    if (!organization || !wiqlQuery) {
      console.error('Organization and WIQL query are required parameters.');
      TelemetryService.trackException(new Error('Missing required parameters for fetchTicketsByQuery'), { organization, project, hasWiqlQuery: !!wiqlQuery });
      return null;
    }

    const orgAndProject = project ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/wiql?api-version=7.0`;

    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ query: wiqlQuery }),
      });

      const duration = Date.now() - startTime;
      const success = response.ok;
      
      TelemetryService.trackApiCall('AzureDevOps_FetchTicketsByQuery', url, duration, success, response.status, {
        organization,
        project,
        statusCode: response.status,
        queryLength: wiqlQuery.length
      });

      if (!response.ok) {
        const errorDetails = await response.text();
        console.error(`Error fetching tickets from Azure DevOps API: ${response.statusText}`, errorDetails);
        throw new Error(`API call failed with status: ${response.status}`);
      }

      const result = await response.json();
      TelemetryService.trackEvent('AzureDevOps_TicketsRetrieved', {
        organization,
        project,
        ticketCount: result.workItems?.length || 0
      });
      return result;
    } catch (error) {
      const duration = Date.now() - startTime;
      TelemetryService.trackApiCall('AzureDevOps_FetchTicketsByQuery', url, duration, false, 0, {
        organization,
        project,
        error: (error as Error).message
      });
      TelemetryService.trackException(error as Error, { method: 'fetchTicketsByQuery', organization, project });
      console.error('Error fetching tickets from Azure DevOps API:', error);
      return null;
    }
  }

  /**
   * Fetches a single work item by its Azure DevOps REST API URL.
   * @param url The full Azure DevOps REST API URL for the work item.
   * @returns The work item details or null if an error occurs.
   */
  async fetchWorkItemByUrl(url: string): Promise<any> {
    // Use in-memory cache to avoid duplicate network calls during a session
    try {
      const cacheKey = `url:${url}`;
      const cached = this.workItemCache.get(cacheKey);
      if (cached && (Date.now() - cached.fetchedAt) < this.WORKITEM_CACHE_TTL_MS) {
        return cached.data;
      }

      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        return null;
      }

      const _url = url + "?$expand=relations";
      const response = await fetch(_url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Error fetching work item from Azure DevOps API: ${response.statusText}`);
      }
      const data = await response.json();
      // Cache by URL and by ID (if present)
      this.workItemCache.set(cacheKey, { fetchedAt: Date.now(), data });
      if (data && data.id) {
        this.workItemCache.set(`id:${data.id}`, { fetchedAt: Date.now(), data });
      }
      return data;
    } catch (error) {
      console.error(`Error fetching work item from Azure DevOps API (${url}): `, error);
      return null;
    }
  }

  /**
   * Updates a set of fields for a ticket (work item) in Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param id The ID of the work item to update.
   * @param fields An object containing the fields to update and their new values.
   * @returns The updated work item details or null if an error occurs.
   */
  async updateTicketFields(organization: string, project: string, id: number, fields: Record<string, any>): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }

    const orgAndProject = project !== "" ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/workitems/${id}?api-version=7.0`;
    const patchDocument = Object.entries(fields).map(([key, value]) => ({
      op: "add",
      path: `/fields/${key}`,
      value,
    }));

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error updating ticket fields: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating ticket fields:", error);
      return null;
    }
  }

  /**
   * Sets the maintenance field for a ticket (work item) in Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param id The ID of the work item to update.
   * @param initiativeName The value to set for the maintenance field.
   * @returns The updated work item details or null if an error occurs.
   */
  async setMaintenanceField(organization: string, project: string, id: number, initiativeName: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }

    const orgAndProject = project !== "" ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/workitems/${id}?api-version=7.0`;
    const patchDocument = [
      {
        op: "add",
        path: "/fields/Custom.Maintenance",
        value: initiativeName,
      },
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error updating maintenance field: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating maintenance field:", error);
      return null;
    }
  }

  /**
   * Creates relations between tickets (work items) in Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param sourceId The ID of the source work item.
   * @param targetId The ID of the target work item.
   * @param relationType The type of relation to create (e.g., 'System.LinkTypes.Related').
   * @returns The updated source work item details or null if an error occurs.
   */
  async createTicketRelation(
    organization: string,
    sourceId: number,
    targetId: number,
    relationType: string
  ): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }

    const url = `https://dev.azure.com/${organization}/_apis/wit/workitems/${sourceId}?api-version=7.0`;
    const patchDocument = [
      {
        op: "add",
        path: "/relations/-",
        value: {
          rel: relationType,
          url: `https://dev.azure.com/${organization}/_apis/wit/workitems/${targetId}`,
        },
      },
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error creating ticket relation: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating ticket relation:", error);
      return null;
    }
  }

  /**
   * Fetches a batch of work items by their IDs from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param ids An array of work item IDs to fetch.
   * @returns The list of work item details or null if an error occurs.
   */
  async fetchWorkItemsBatch(organization: string, project: string, ids: number[]): Promise<any[]> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return [];
    }

    const orgAndProject = project !== "" ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/workitemsbatch?api-version=7.0`;
    const requestBody = {
      ids,
      fields: ["System.Id", "System.Title", "System.State", "System.WorkItemType", "System.TeamProject", "System.ChangedDate"],
    };

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        throw new Error(`Error fetching work items batch: ${response.statusText}`);
      }

      const result = await response.json();
      return result.value || [];
    } catch (error) {
      console.error("Error fetching work items batch:", error);
      return [];
    }
  }

  /**
   * Fetches a work item by its ID from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param id The ID of the work item to fetch.
   * @returns The work item details or null if an error occurs.
   */
  async fetchWorkItemById(id: number): Promise<any> {
    try {
      const cacheKey = `id:${id}`;
      const cached = this.workItemCache.get(cacheKey);
      if (cached && (Date.now() - cached.fetchedAt) < this.WORKITEM_CACHE_TTL_MS) {
        return cached.data;
      }

      const accessToken = await this.getAccessToken();
      if (!accessToken) {
        return null;
      }
      const organization = "sensio";
      const url = `https://dev.azure.com/${organization}/_apis/wit/workitems/${id}?$expand=relations&api-version=7.0`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        throw new Error(`Error fetching work item by ID from Azure DevOps API: ${response.statusText}`);
      }
      const data = await response.json();
      this.workItemCache.set(cacheKey, { fetchedAt: Date.now(), data });
      if (data && data.url) {
        this.workItemCache.set(`url:${data.url}`, { fetchedAt: Date.now(), data });
      }
      return data;
    } catch (error) {
      console.error(`Error fetching work item by ID from Azure DevOps API (${id}): `, error);
      return null;
    }
  }

  /**
   * Updates a ticket's fields using Azure DevOps API.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param ticketId The ID of the ticket (work item) to update.
   * @param fields An object containing the fields to update and their new values.
   * @returns The updated work item details or null if an error occurs.
   */
  async updateTicket(ticketId: number, fields: Record<string, any>): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to update the ticket.");
    }

    const organization = "sensio"
    const project = "";
    const orgAndProject = project !== "" ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/workitems/${ticketId}?api-version=7.0`;
    const patchDocument = Object.keys(fields).map((key) => ({
      op: "add",
      path: `/fields/${key}`,
      value: fields[key],
    }));

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error updating ticket: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error updating ticket:", error);
      return null;
    }
  }

  /**
   * Adds a comment to a work item in Azure DevOps using System.History field.
   * @param workItemId The ID of the work item to add comment to.
   * @param commentText The text of the comment to add.
   * @returns The updated work item details or null if an error occurs.
   */
  async addComment(workItemId: number, commentText: string): Promise<any> {
    console.log(`[DEBUG] addComment called with workItemId: ${workItemId}, commentText: "${commentText}"`);
    
    const accessToken = await this.getAccessToken();
    console.log(`[DEBUG] Access token retrieved: ${accessToken ? 'YES' : 'NO'}`);
    if (!accessToken) {
      console.error("[DEBUG] No access token available");
      throw new Error("Access token is required to add comment.");
    }

    const organization = "sensio";
    const project = "";
    const orgAndProject = project !== "" ? `${organization}/${project}` : organization;
    const url = `https://dev.azure.com/${orgAndProject}/_apis/wit/workitems/${workItemId}?api-version=7.0`;
    
    console.log(`[DEBUG] API URL: ${url}`);
    
    const patchDocument = [{
      op: "add",
      path: "/fields/System.History",
      value: commentText
    }];
    
    console.log(`[DEBUG] Request body:`, patchDocument);

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      console.log(`[DEBUG] Response status: ${response.status} ${response.statusText}`);

      if (!response.ok) {
        const errorBody = await response.text();
        console.error(`[DEBUG] System.History API Error - Status: ${response.status}, StatusText: ${response.statusText}, Body:`, errorBody);
        throw new Error(`Error adding comment: ${response.status} ${response.statusText} - ${errorBody}`);
      }

      const responseData = await response.json();
      console.log(`[DEBUG] Success! Response data:`, responseData);
      return responseData;
    } catch (error) {
      console.error("[DEBUG] Error in addComment:", error);
      return null;
    }
  }

  /**
   * Moves and extends the iteration dates of a ticket (work item) in Azure DevOps.
   * @param auth The authentication object.
   * @param selectedTicket The selected ticket (work item) object.
   * @param moveNumberOfDays The number of days to move the start date forward.
   * @param extendNumberOfDays The number of days to extend the end date.
   * @returns The updated work item details or null if an error occurs.
   */
  async moveAndExtendTicketIteration(selectedTicket: any, moveNumberOfDays: number, extendNumberOfDays: number): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to move and extend the ticket iteration.");
    }

    const organization = "sensio";
    const url = `https://dev.azure.com/${organization}/_apis/wit/workitems/${selectedTicket.id}?api-version=7.0`;

    const newStartDate = new Date(new Date(selectedTicket.startIteration).getTime() + moveNumberOfDays * 24 * 60 * 60 * 1000);
    const newEndDate = new Date(new Date(selectedTicket.endIteration).getTime() + (moveNumberOfDays + extendNumberOfDays) * 24 * 60 * 60 * 1000);

    const patchDocument = [
      {
        op: "replace",
        path: "/fields/"+AF_START_ITERATION,
        value: newStartDate.toISOString(),
      },
      {
        op: "replace",
        path: "/fields/"+AF_END_ITERATION,
        value: newEndDate.toISOString(),
      },
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error moving and extending ticket iteration: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error moving and extending ticket iteration:", error);
      return null;
    }
  }

  /**
   * Removes a ticket (work item) from the timeline in Azure DevOps.
   * @param ticketId The ID of the ticket (work item) to remove from the timeline.
   * @returns A promise that resolves when the operation is complete.
   */
  async removeTicketFromTimeline(ticketId: number): Promise<void> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to remove the ticket from the timeline.");
    }

    const organization = "sensio";
    const url = `https://dev.azure.com/${organization}/_apis/wit/workitems/${ticketId}?api-version=7.0`;

    const patchDocument = [
      {
        op: "remove",
        path: "/fields/"+ AF_START_ITERATION,
      },
      {
        op: "remove",
        path: "/fields/"+ AF_END_ITERATION,
      },
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error removing ticket from timeline: ${response.statusText}`);
      }

      console.log(`Ticket ${ticketId} successfully removed from timeline.`);
    } catch (error) {
      console.error("Error removing ticket from timeline:", error);
    }
  }


  async addPlanningTask(selectedTicket: Ticket, spec: string, project : string, ticketType : string) : Promise<void> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to remove the ticket from the timeline.");
    }


      const url = tConst.AZURE_DEVOPS_BASE_URI + project + "/_apis/wit/workitems/$" + ticketType + "?api-version=7.1-preview.3"

      const _end = new Date(selectedTicket?.fields[AF_END_ITERATION]);
      const _start = new Date(selectedTicket?.fields[AF_START_ITERATION]);
      const _dateDiff = date_diff_indays(_start, _end);
      const _PlanningPriority = 0.1 + Number(selectedTicket.planningPri);

      const _title = spec + ": " + selectedTicket.title;


      const _FuncSpec = tConst.AZURE_SPECS[0];
    
      if (spec === _FuncSpec) {
        _end.setDate(_end.getDate() - (14 + _dateDiff));
        _start.setDate(_start.getDate() - 28);
      } else {
        _end.setDate(_end.getDate() - _dateDiff);
        _start.setDate(_start.getDate() - 14);
      }

      
      const patchDocument = [
        {
            "op": "add",
            "path": "/fields/"+AF_TITLE,
            "from": null,
            "value": _title
        },
        {
            "op": "add",
            "path": "/relations/-",
            "value": {
                "rel": AF_REL_PARENT,

                "url": tConst.AZURE_DEVOPS_BASE_URI + selectedTicket.project + "/_apis/wit/workItems/" + selectedTicket.id
            }
        },
        {
            "op": "add",
            "path": "/fields/" + AF_INITIATIV,
            "value": selectedTicket.initiative
        },
        {
            "op": "add",
            "path": "/fields/" + AF_PLANNING_PRIORITY,
            "value": _PlanningPriority
        },
        {
            "op": "add",
            "path": "/fields/" + AF_START_ITERATION,
            "value": _start
        },
        {
            "op": "add",
            "path": "/fields/" + AF_END_ITERATION,
            "value": _end
        },
        {
            "op": "add",
            "path": "/fields/" + AF_PLANNING_STATUS,
            "value": "9. N/A"
        }
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error adding specs: ${response.statusText}`);
      }

      console.log(`Ticket Planning Task successfully added for ${selectedTicket.id} in project ${project}.`);
    } catch (error) {
      console.error("Error adding specs:", error);
    }
  }

  /**
   * Creates a new ticket (work item) in Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param ticketType The type of the ticket (e.g., 'Task', 'Bug').
   * @param fields An object containing the fields to set for the new ticket.
   * @returns The created work item details or null if an error occurs.
   */
  async createTicket(project: string, ticketType: string, fields: Record<string, any>, parentUrl: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to create a ticket.");
    }

    const url = `https://dev.azure.com/sensio/${project}/_apis/wit/workitems/$${ticketType}?api-version=7.0`;
    const patchDocument = Object.entries(fields).map(([key, value]) => ({
      op: "add",
      path: `${key}`,
      value,
    }));

    if (parentUrl && parentUrl !== "") {
      patchDocument.push({
        op: "add", 
        path: "/relations/-",
        value: {
          rel: AF_RELATION_PARENT,
          url: parentUrl,
        },
      })
    }
 

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error creating ticket: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error("Error creating ticket:", error);
      return null;
    }
  }

  /**
   * Adds a ticket to a sprint in Azure DevOps.
   * @param ticketId The ID of the ticket (work item) to add to the sprint.
   * @param startDate The start date of the sprint.
   * @param endDate The end date of the sprint.
   * @returns A promise that resolves when the operation is complete.
   */
  async addTicketToSprint(ticketId: string, startDate: string, endDate: string): Promise<void> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      throw new Error("Access token is required to add the ticket to the sprint.");
    }

    const organization = "sensio";
    const url = `https://dev.azure.com/${organization}/_apis/wit/workitems/${ticketId}?api-version=7.0`;

    const patchDocument = [
      {
        op: "add",
        path: "/fields/Custom.StartIteration",
        value: startDate,
      },
      {
        op: "add",
        path: "/fields/Custom.EndIteration",
        value: endDate,
      },
    ];

    try {
      const response = await fetch(url, {
        method: "PATCH",
        headers: {
          Authorization: `Bearer ${accessToken}`,
          "Content-Type": "application/json-patch+json",
        },
        body: JSON.stringify(patchDocument),
      });

      if (!response.ok) {
        throw new Error(`Error adding ticket to sprint: ${response.statusText}`);
      }

      console.log(`Ticket ${ticketId} successfully added to sprint.`);
    } catch (error) {
      console.error("Error adding ticket to sprint:", error);
    }
  }

  /**
   * Fetches all users from Azure DevOps Graph API.
   * @param organization The Azure DevOps organization name.
   * @returns The list of users or empty array if an error occurs.
   */
  async fetchAllUsers(organization: string): Promise<any[]> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return [];
    }

    const url = `https://vssps.dev.azure.com/${organization}/_apis/graph/users?api-version=7.1-preview.1&subjectTypes=aad`;
    
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });

      if (!response.ok) {
        console.warn(`Error fetching users from Azure DevOps Graph API: ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      return result.value || [];
    } catch (error) {
      console.error('Error fetching users from Azure DevOps Graph API:', error);
      return [];
    }
  }

  /**
   * Fetches field metadata from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param fieldName The field name or reference name.
   * @returns The field metadata or null if an error occurs.
   */
  async fetchFieldMetadata(organization: string, project: string, fieldName: string): Promise<any> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return null;
    }
    
    const url = `https://dev.azure.com/${organization}/${project}/_apis/wit/fields/${encodeURIComponent(fieldName)}?api-version=7.1`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        console.warn(`Error fetching field metadata for ${fieldName}: ${response.statusText}`);
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching field metadata:', error);
      return null;
    }
  }

  /**
   * Fetches picklist values from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param listId The picklist ID.
   * @returns The list of picklist values or empty array if an error occurs.
   */
  async fetchPicklistValues(organization: string, listId: string): Promise<string[]> {
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      return [];
    }
    
    const url = `https://dev.azure.com/${organization}/_apis/work/processes/lists/${listId}?api-version=7.1`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });
      if (!response.ok) {
        console.warn(`Error fetching picklist values for ${listId}: ${response.statusText}`);
        return [];
      }
      const result = await response.json();
      return result.items || [];
    } catch (error) {
      console.error('Error fetching picklist values:', error);
      return [];
    }
  }

  /**
   * Fetches CapEx options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of CapEx options or empty array if an error occurs.
   */
  async fetchCapexOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.CapEx');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('CapEx field is not a picklist or picklistId not found');
        return [];
      }
      
      return await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
    } catch (error) {
      console.error('Error fetching CapEx options:', error);
      return [];
    }
  }

  /**
   * Fetches Gov Project options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of Gov Project options or empty array if an error occurs.
   */
  async fetchGovProjectOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.GovProject');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('GovProject field is not a picklist or picklistId not found');
        return [];
      }
      
      return await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
    } catch (error) {
      console.error('Error fetching Gov Project options:', error);
      return [];
    }
  }

  /**
   * Fetches Priority Driver options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of Priority Driver options sorted in custom order or empty array if an error occurs.
   */
  async fetchPriorityDriverOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.PriorityDriver');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('PriorityDriver field is not a picklist or picklistId not found');
        return [];
      }
      
      const rawOptions = await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
      
      const priorityItems: string[] = [];
      const remainingItems: string[] = [];
      let otherItem: string | null = null;
      
      rawOptions.forEach(option => {
        const priorityIndex = PRIORITY_DRIVERS_ORDER.indexOf(option);
        if (priorityIndex !== -1) {
          priorityItems[priorityIndex] = option;
        } else if (option.toLowerCase() === 'other') {
          otherItem = option;
        } else {
          remainingItems.push(option);
        }
      });
      
      const sortedOptions = [
        ...priorityItems.filter(item => item !== undefined),
        ...remainingItems.sort(),
        ...(otherItem ? [otherItem] : [])
      ];
      
      return sortedOptions;
    } catch (error) {
      console.error('Error fetching Priority Driver options:', error);
      return [];
    }
  }

  async fetchInitiativeOptions(organization: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, 'RnD Planning', 'Custom.Initiative');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('Initiative field is not a picklist or picklistId not found');
        return [];
      }
      
      const rawOptions = await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
      return rawOptions.sort((a, b) => a.localeCompare(b));
    } catch (error) {
      console.error('Error fetching Initiative options:', error);
      return [];
    }
  }

  /**
   * Fetches Product options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of Product options or empty array if an error occurs.
   */
  async fetchProductOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.SensioProduct');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('Product field is not a picklist or picklistId not found');
        return [];
      }
      
      return await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
    } catch (error) {
      console.error('Error fetching Product options:', error);
      return [];
    }
  }

  /**
   * Fetches Investment Class options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of Investment Class options or empty array if an error occurs.
   */
  async fetchInvestmentClassOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.Investmentclass');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('Investment Class field is not a picklist or picklistId not found');
        return [];
      }
      
      return await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
    } catch (error) {
      console.error('Error fetching Investment Class options:', error);
      return [];
    }
  }

  private async getFieldWithParentLookup(workItem: any, fieldName: string, workItemCache: Map<string, any>, maxDepth: number = 5): Promise<{ value: string; inherited: boolean }> {
    if (maxDepth <= 0) {
      return { value: '', inherited: false };
    }

    const directValue = workItem.fields?.[fieldName];
    if (directValue && directValue.trim() !== '') {
      return { value: directValue, inherited: false };
    }

    const parentUrl = workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
    if (parentUrl) {
      try {
        let parentWorkItem = workItemCache.get(parentUrl);
        if (!parentWorkItem) {
          parentWorkItem = await this.fetchWorkItemByUrl(parentUrl);
          if (parentWorkItem) {
            workItemCache.set(parentUrl, parentWorkItem);
          }
        }
        
        if (parentWorkItem) {
          const parentResult = await this.getFieldWithParentLookup(parentWorkItem, fieldName, workItemCache, maxDepth - 1);
          if (parentResult.value && parentResult.value.trim() !== '') {
            return { value: parentResult.value, inherited: true };
          }
        }
      } catch (error) {
        console.warn(`Error fetching parent work item from ${parentUrl}:`, error);
      }
    }

    return { value: '', inherited: false };
  }

  /**
   * Fetches the complete parent chain for a work item recursively
   * @param workItem The work item to fetch parents for
   * @param workItemCache Cache to avoid repeated API calls
   * @param maxDepth Maximum depth to prevent infinite loops
   * @returns Array of parent work items from immediate parent to root
   */
  async fetchParentChain(workItem: any, workItemCache: Map<string, any>, maxDepth: number = 10): Promise<any[]> {
    if (maxDepth <= 0) {
      return [];
    }

    const parentUrl = workItem.relations?.find((rel: any) => rel.rel === 'System.LinkTypes.Hierarchy-Reverse')?.url;
    
    if (!parentUrl) {
      return [];
    }

    try {
      let parentWorkItem = workItemCache.get(parentUrl);
      if (!parentWorkItem) {
        parentWorkItem = await this.fetchWorkItemByUrl(parentUrl);
        if (parentWorkItem) {
          workItemCache.set(parentUrl, parentWorkItem);
        }
      }

      if (parentWorkItem) {
        const grandParents = await this.fetchParentChain(parentWorkItem, workItemCache, maxDepth - 1);
        return [parentWorkItem, ...grandParents];
      }
    } catch (error) {
      console.warn(`Error fetching parent work item from ${parentUrl}:`, error);
    }

    return [];
  }

  /**
   * Gets inherited fields for a work item using parent lookup
   * @param workItem The work item to get inherited fields for
   * @param workItemCache Cache to avoid repeated API calls
   * @returns Object with inherited field values and inheritance status
   */
  async getInheritedFields(workItem: any, workItemCache: Map<string, any>): Promise<{
    govProject: { value: string; inherited: boolean };
    capex: { value: string; inherited: boolean };
    priorityDriver: { value: string; inherited: boolean };
    product: { value: string; inherited: boolean };
    investmentClass: { value: string; inherited: boolean };
  }> {
    const [govProject, capex, priorityDriver, product, investmentClass] = await Promise.all([
      this.getFieldWithParentLookup(workItem, 'Custom.GovProject', workItemCache),
      this.getFieldWithParentLookup(workItem, 'Custom.CapEx', workItemCache),
      this.getFieldWithParentLookup(workItem, 'Custom.PriorityDriver', workItemCache),
      this.getFieldWithParentLookup(workItem, 'Custom.SensioProduct', workItemCache),
      this.getFieldWithParentLookup(workItem, 'Custom.Investmentclass', workItemCache)
    ]);

    return { govProject, capex, priorityDriver, product, investmentClass };
  }

  /**
<<<<<<< HEAD
<<<<<<< HEAD
<<<<<<< HEAD
   * Fetches Demo options from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @returns The list of Demo options or empty array if an error occurs.
   */
  async fetchDemoOptions(organization: string, project: string): Promise<string[]> {
    try {
      const fieldMetadata = await this.fetchFieldMetadata(organization, project, 'Custom.Demo');
      if (!fieldMetadata || !fieldMetadata.isPicklist || !fieldMetadata.picklistId) {
        console.warn('Demo field is not a picklist or picklistId not found');
        return [];
      }
      
      const rawOptions = await this.fetchPicklistValues(organization, fieldMetadata.picklistId);
      
      const demoOrder = ['No', 'Candidate', 'Planned', 'Done'];
      
      const orderedItems: string[] = [];
      const remainingItems: string[] = [];
      
      demoOrder.forEach(orderItem => {
        const foundItem = rawOptions.find(option => option === orderItem);
        if (foundItem) {
          orderedItems.push(foundItem);
        }
      });
      
      rawOptions.forEach(option => {
        if (!demoOrder.includes(option)) {
          remainingItems.push(option);
        }
      });
      
      remainingItems.sort();
      
      return [...orderedItems, ...remainingItems];
    } catch (error) {
      console.error('Error fetching Demo options:', error);
      return [];
    }
  }

  /**
   * Fetches iterations for a team from Azure DevOps.
   * @param organization The Azure DevOps organization name.
   * @param project The Azure DevOps project name.
   * @param team The team name or ID (optional, defaults to project team).
   * @returns The list of iterations or empty array if an error occurs.
   */
  async fetchIterations(organization: string, project: string, team?: string): Promise<TeamSettingsIteration[]> {
    const cacheKey = `${organization}::${project}::${team || ''}`;
    const memCached = this.iterationsCache.get(cacheKey);
    if (memCached && (Date.now() - memCached.fetchedAt) < this.ITERATIONS_CACHE_TTL_MS) {
      return memCached.data;
    }

    // Check localStorage
    try {
      const storageKey = `AzureDevOpsService-iterations-${organization}-${project}-${team || ''}`;
      const storageTsKey = `${storageKey}-ts`;
      const cachedStr = localStorage.getItem(storageKey);
      const cachedTs = localStorage.getItem(storageTsKey);
      if (cachedStr && cachedTs) {
        const age = Date.now() - parseInt(cachedTs);
        if (age < this.ITERATIONS_CACHE_TTL_MS) {
          const parsed = JSON.parse(cachedStr);
          this.iterationsCache.set(cacheKey, { fetchedAt: Date.now(), data: parsed });
          return parsed;
        }
      }
    } catch (err) {
      // ignore localStorage errors
    }

    const startTime = Date.now();
    const accessToken = await this.getAccessToken();
    if (!accessToken) {
      TelemetryService.trackException(new Error('No access token for fetchIterations'), { organization, project, team });
      return [];
    }

    const teamPath = team ? `/${encodeURIComponent(team)}` : '';
    const url = `https://dev.azure.com/${organization}/${encodeURIComponent(project)}${teamPath}/_apis/work/teamsettings/iterations?api-version=7.1`;
    try {
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Accept': 'application/json',
        },
      });

      const duration = Date.now() - startTime;
      const success = response.ok;
      TelemetryService.trackApiCall('AzureDevOps_FetchIterations', url, duration, success, response.status, {
        organization,
        project,
        team,
        statusCode: response.status
      });

      if (!response.ok) {
        console.warn(`Error fetching iterations from Azure DevOps API: ${response.status} ${response.statusText}`);
        return [];
      }

      const result = await response.json();
      const iterations = result.value || [];
      this.iterationsCache.set(cacheKey, { fetchedAt: Date.now(), data: iterations });
      try {
        const storageKey = `AzureDevOpsService-iterations-${organization}-${project}-${team || ''}`;
        const storageTsKey = `${storageKey}-ts`;
        localStorage.setItem(storageKey, JSON.stringify(iterations));
        localStorage.setItem(storageTsKey, Date.now().toString());
      } catch (err) {
        // ignore localStorage errors
      }
      console.log(`Fetched ${iterations.length} iterations for project ${project}${team ? ` team ${team}` : ''}:`, iterations);
      return iterations;
    } catch (error) {
      const duration = Date.now() - startTime;
      TelemetryService.trackApiCall('AzureDevOps_FetchIterations', url, duration, false, 0, {
        organization,
        project,
        team,
        error: (error as Error).message
      });
      TelemetryService.trackException(error as Error, { method: 'fetchIterations', organization, project, team });
      console.error('Error fetching iterations from Azure DevOps API:', error);
      return [];
    }
  }

  /**
   * Fetches work items with time tracking data from the last 200 days using Azure DevOps native fields.
   * Uses WIQL to query work items that have time tracking fields populated.
   * @param organization The Azure DevOps organization name.
   * @param onProgressUpdate Optional callback to receive progressive updates as work items are processed.
   * @returns Array of work items with their time tracking data or empty array if an error occurs.
   */
  async fetchWorkItemsWithTimeLogs(organization: string, onProgressUpdate?: (items: WorkItemWithTimeLog[]) => void): Promise<WorkItemWithTimeLog[]> {
    try {
      const today = new Date();
      const twoHundredDaysAgo = new Date(today.getTime() - (200 * 24 * 60 * 60 * 1000));
      
      const fromDate = twoHundredDaysAgo.toISOString().split('T')[0];
      
      const wiql = `
        SELECT [System.Id], [System.Title], [System.State], [System.WorkItemType], [System.TeamProject], 
               [System.AssignedTo], [System.ChangedDate], [Microsoft.VSTS.Scheduling.CompletedWork], 
               [Microsoft.VSTS.Scheduling.OriginalEstimate], [Microsoft.VSTS.Scheduling.RemainingWork],
               [Microsoft.VSTS.Scheduling.Effort], [Microsoft.VSTS.Scheduling.StoryPoints],
               [Custom.GovProject], [Custom.CapEx], [Custom.PriorityDriver], [Custom.Initiative]
        FROM WorkItems 
        WHERE [System.ChangedDate] >= '${fromDate}' 
          AND (
            [Microsoft.VSTS.Scheduling.CompletedWork] <> '' 
            OR [Microsoft.VSTS.Scheduling.OriginalEstimate] <> ''
            OR [Microsoft.VSTS.Scheduling.RemainingWork] <> ''
            OR [Microsoft.VSTS.Scheduling.Effort] <> ''
            OR [Microsoft.VSTS.Scheduling.StoryPoints] <> ''
          )
        ORDER BY [System.ChangedDate] DESC
      `;

      console.log('Fetching work items with time tracking data using WIQL query...');
      
      const result = await this.fetchTicketsByQuery(organization, '', wiql);
      
      if (!result || !Array.isArray(result.workItems) || result.workItems.length === 0) {
        console.log('No work items found with time tracking data in the last 200 days');
        return [];
      }

      console.log(`Found ${result.workItems.length} work items with time tracking data`);
      
      const workItemsWithLogs: WorkItemWithTimeLog[] = [];
      const workItemCache = new Map<string, any>();
      const batchSize = 10; // Process in batches for progressive loading
      
      for (let i = 0; i < result.workItems.length; i++) {
        const item = result.workItems[i];
        try {
          if (item.url) {
            const workItem = await this.fetchWorkItemByUrl(item.url);
            
            if (workItem && workItem.fields) {
              const completedWork = workItem.fields['Microsoft.VSTS.Scheduling.CompletedWork'] || 0;
              const originalEstimate = workItem.fields['Microsoft.VSTS.Scheduling.OriginalEstimate'] || 0;
              const remainingWork = workItem.fields['Microsoft.VSTS.Scheduling.RemainingWork'] || 0;
              const effort = workItem.fields['Microsoft.VSTS.Scheduling.Effort'] || 0;
              const storyPoints = workItem.fields['Microsoft.VSTS.Scheduling.StoryPoints'] || 0;
              
              let totalDuration = 0;
              const timeLogs: WorklogItem[] = [];
              
              if (completedWork > 0) {
                totalDuration += completedWork;
                timeLogs.push({
                  id: `${workItem.id}-completed`,
                  workItemId: workItem.id,
                  timestamp: workItem.fields['System.ChangedDate'] || new Date().toISOString(),
                  length: completedWork * 3600,
                  comment: 'Completed Work',
                  user: {
                    id: workItem.fields['System.AssignedTo']?.id || '',
                    uniqueName: workItem.fields['System.AssignedTo']?.uniqueName || '',
                    displayName: workItem.fields['System.AssignedTo']?.displayName || 'Unassigned',
                    email: workItem.fields['System.AssignedTo']?.uniqueName || '',
                    name: workItem.fields['System.AssignedTo']?.displayName || 'Unassigned'
                  }
                });
              }
              
              if (originalEstimate > 0) {
                timeLogs.push({
                  id: `${workItem.id}-estimate`,
                  workItemId: workItem.id,
                  timestamp: workItem.fields['System.CreatedDate'] || new Date().toISOString(),
                  length: originalEstimate * 3600,
                  comment: 'Original Estimate',
                  user: {
                    id: workItem.fields['System.CreatedBy']?.id || '',
                    uniqueName: workItem.fields['System.CreatedBy']?.uniqueName || '',
                    displayName: workItem.fields['System.CreatedBy']?.displayName || 'Unknown',
                    email: workItem.fields['System.CreatedBy']?.uniqueName || '',
                    name: workItem.fields['System.CreatedBy']?.displayName || 'Unknown'
                  }
                });
              }
              
              if (totalDuration > 0 || effort > 0 || storyPoints > 0) {
                const govProject = await this.getFieldWithParentLookup(workItem, 'Custom.GovProject', workItemCache);
                const capex = await this.getFieldWithParentLookup(workItem, 'Custom.CapEx', workItemCache);
                const priorityDriver = await this.getFieldWithParentLookup(workItem, 'Custom.PriorityDriver', workItemCache);
                const product = await this.getFieldWithParentLookup(workItem, 'Custom.SensioProduct', workItemCache);
                const investmentClass = await this.getFieldWithParentLookup(workItem, 'Custom.Investmentclass', workItemCache);

                workItemsWithLogs.push({
                  workItem,
                  timeLogs,
                  totalDuration: totalDuration * 3600,
                  inheritedFields: {
                    govProject,
                    capex,
                    priorityDriver,
                    product,
                    investmentClass
                  }
                });
              }
            }
          }
        } catch (error) {
          console.warn(`Error fetching work item details for ${item.id}:`, error);
        }
        
        if (onProgressUpdate && (i + 1) % batchSize === 0) {
          const sortedBatch = [...workItemsWithLogs].sort((a, b) => b.totalDuration - a.totalDuration);
          onProgressUpdate(sortedBatch);
        }
      }

      console.log(`Successfully processed ${workItemsWithLogs.length} work items with time tracking data`);
      return workItemsWithLogs.sort((a, b) => b.totalDuration - a.totalDuration);

    } catch (error) {
      console.error('Error fetching work items with time tracking data:', error);
      return [];
    }
  }
}

export default AzureDevOpsService;
